import React, { useEffect, useState } from 'react';
import ImportantFieldsSection from '../../ImportantFieldsSection';
import {
  Building,
  Calendar2Check,
  ExclamationDiamondFill,
  Hash,
  InfoCircle,
  Person,
  Envelope,
  Telephone
} from 'react-bootstrap-icons';
import moment from 'moment';
import SearchableDropdown from '../../common/SearchSelectable';
import useAppChoices from '../../../hooks/useAppChoices';
import { makeRESTApiRequest } from '../../../helpers/api';
import { ENDPOINTS, SUBMIT_STATUS } from '../../../helpers/constants';
import { toast } from 'sonner';
import { Check, X } from 'react-bootstrap-icons';
import Spinner from 'react-bootstrap/Spinner';
import AddOrganizationForm from './addOrganization/AddOrganizationForm';
import { isStaff } from '../../../helpers/global';

export const getOrganizationTooltipText = organization => {
  const tooltipData = [
    { label: 'Business Source', value: organization?.businessSource || 'N/A' },
    { label: 'Account Manager', value: organization?.accountManager?.name || 'N/A' }
  ];

  return (
    <>
      {tooltipData.map((item, index) => (
        <React.Fragment key={`org-tooltip-${index}`}>
          <b>{item.label}:</b> {item.value}
          <br />
          <br />
        </React.Fragment>
      ))}
    </>
  );
};

export const getPublicServiceTooltipText = service => {
  const tooltipData = [
    { label: 'Requesting Person Name', value: service?.requestingPersonName || 'N/A' },
    { label: 'Requesting Person Email', value: service?.requestingPersonEmail || 'N/A' },
    { label: 'Requesting Person Phone', value: service?.requestingPersonPhone || 'N/A' },
    { label: 'Organization Name', value: service?.organizationName || 'N/A' },
    { label: 'Has Worked With Us Before', value: service?.hasWorkedWithUsBefore ? 'Yes' : 'No' }
  ];

  return (
    <>
      {tooltipData.map((item, index) => (
        <React.Fragment key={`public-tooltip-${index}`}>
          <b>{item.label}:</b> {item.value}
          <br />
          <br />
        </React.Fragment>
      ))}
    </>
  );
};

const ServiceDetails = ({ service, isSelecting = false, onServiceUpdate }) => {
  const [currentUserInfo] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [itemActionMeta, setItemActionMeta] = useState(null);
  const orgFromOptions = useAppChoices('organizations');
  const [orgs, setOrgs] = useState(orgFromOptions);
  const [newOrg, setNewOrg] = useState(null);

  const handleOrganizationClick = () => {
    if (isStaff(currentUserInfo.role) && service?.organization?._id) {
      window.open(`/dashboard?organizationId=${service.organization._id}`, '_blank');
    }
  };

  return (
    <div className="my-2">
      {/* FOLIO NUMBER */}
      <ImportantFieldsSection
        colWidth={6}
        isSelecting={isSelecting}
        title="Service Details"
        fields={[
          {
            Icon: Hash,
            title: 'Accurate ID',
            showTitle: false,
            value: service['accurateId']
          },
          {
            Icon: InfoCircle,
            title: 'Folio Numbers',
            value: service['folioNumbers'] && service['folioNumbers'].join(', ')
          },
          { Icon: InfoCircle, title: 'File Name/Number', value: service['fileName'] },
          { Icon: InfoCircle, title: 'County', value: service['county'] },

          {
            Icon: Calendar2Check,
            title: 'Closing Date',
            value: service['closingDate'] && moment(service['closingDate']).format('MMMM Do YYYY')
          },

          // Individual Service Fields
          ...(service?.isIndividualService
            ? [
              {
                Icon: Person,
                title: 'Individual Name',
                value: service?.individual?.name
              },
              {
                Icon: Envelope,
                title: 'Individual Email',
                value: service?.individual?.email
              },
              {
                Icon: Telephone,
                title: 'Individual Phone',
                value: service?.individual?.phoneNumber
              }
            ]
            : [
              {
                id: service,
                Icon: Building,
                title: 'Organization',
                value: service?.organization?.name,
                tooltipText: service?.organization
                  ? getOrganizationTooltipText(service?.organization)
                  : getPublicServiceTooltipText(service),
                isNotFoundComponentAvailable: !service?.organization?.name,
                NotFoundComponent: service?.organization?.name ? null : (
                  <AddOrganization
                    newOrg={newOrg}
                    setNewOrg={setNewOrg}
                    id={service?._id}
                    orgs={orgs}
                    onServiceUpdate={onServiceUpdate}
                    setItemActionMeta={setItemActionMeta}
                  />
                ),
                onClick: handleOrganizationClick
              }
            ])
        ]}
      />
      <hr />
      {/* Modal to add new organization with admin */}
      <AddOrganizationForm
        name={itemActionMeta?.initialValue}
        adminName={service?.requestingPersonName}
        adminEmail={service?.requestingPersonEmail}
        setItemActionMeta={setItemActionMeta}
        orgs={orgs}
        setOrgs={setOrgs}
        setNewOrg={setNewOrg}
      />
    </div>
  );
};

export default ServiceDetails;

export const AddOrganization = ({ id, orgs, onServiceUpdate, setItemActionMeta, newOrg, setNewOrg }) => {
  const [status, setStatus] = useState(SUBMIT_STATUS.IDLE);

  const onSelect = async orgId => {
    setStatus(SUBMIT_STATUS.LOADING);
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.SERVICE_WITH_ID(id),
        method: 'PUT',
        requestBody: {
          status: 'Active',
          organization: orgId
        }
      });
      if (error) {
        throw error;
      }
      onServiceUpdate && onServiceUpdate(response);
      setStatus(SUBMIT_STATUS.SUCCESS);
      toast.success('Organization successfully added');
    } catch (error) {
      toast.error('Organization add failed');
      setStatus(SUBMIT_STATUS.FAILED);
    } finally {
      setTimeout(() => {
        setStatus(SUBMIT_STATUS.IDLE);
      }, 1000);
    }
  };
  const options = orgs?.map(item => {
    return { label: item?.name, value: item?._id };
  });

  useEffect(() => {
    if (newOrg) {
      onSelect(newOrg?._id);
      setNewOrg(null);
    }
  }, [newOrg]);

  return (
    <>
      <SearchableDropdown
        options={options}
        onSelect={onSelect}
        label={
          <span className="d-inline-flex align-items-center">
            <ExclamationDiamondFill className="text-danger mx-1" /> No Organization, Tap to select one
          </span>
        }
        onCreateNew={searchText => setItemActionMeta({ initialValue: searchText })}
      />
      {status === SUBMIT_STATUS.FAILED ? (
        <X className="submit-btn-status-icon" />
      ) : status === SUBMIT_STATUS.LOADING ? (
        <Spinner size="sm" animation="border" role="status" className="submit-btn-status-icon spinner" />
      ) : status === SUBMIT_STATUS.SUCCESS ? (
        <Check className="submit-btn-status-icon" />
      ) : null}
    </>
  );
};
